import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { useDroppable } from '@dnd-kit/core'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  Users, Search, UserPlus, ShieldAlert, Settings,
  Phone, Flame, Thermometer, Snowflake, GripVertical,
  Bell, CheckSquare, Square, X, Filter, Send
} from 'lucide-react'

interface Lead {
  id: number
  name: string
  email: string | null
  phone: string | null
  pipeline: string
  pipeline_label: string
  status: string
  status_label: string
  source: string
  source_label: string
  temperature: string | null
  temperature_label: string | null
  reason: string | null
  created_at: string
  converted_at: string | null
  lesson_package?: { id: number; name: string }
  student?: { id: number; name: string; email: string }
}

interface Props {
  columns: Record<string, Lead[]>
  column_order: string[]
  status_counts: Record<string, number>
  retention_new_count: number
  pipeline: string
  search: string | null
  reason_filter: string | null
  package_filter: string | null
  available_packages: { id: number; name: string }[]
}

const COLUMN_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  lesson_scheduled: { label: 'Aula Experimental', color: 'text-purple-600', bg: 'bg-purple-500' },
  lesson_done: { label: 'Fez Aula / Sem Plano', color: 'text-orange-600', bg: 'bg-orange-500' },
  converted: { label: 'Assinou Plano', color: 'text-green-600', bg: 'bg-green-500' },
  lost: { label: 'Perdido', color: 'text-red-600', bg: 'bg-red-500' },
}

const RETENTION_COLUMN_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  new_lead: { label: 'Em Risco', color: 'text-red-600', bg: 'bg-red-500' },
  contacted: { label: 'Contatado', color: 'text-yellow-600', bg: 'bg-yellow-500' },
  converted: { label: 'Resolvido', color: 'text-green-600', bg: 'bg-green-500' },
  lost: { label: 'Perdido', color: 'text-gray-600', bg: 'bg-gray-500' },
}

const TEMP_ICON: Record<string, { icon: typeof Flame; color: string }> = {
  hot: { icon: Flame, color: 'text-red-500' },
  warm: { icon: Thermometer, color: 'text-orange-500' },
  cold: { icon: Snowflake, color: 'text-blue-400' },
}

// Droppable Column
function KanbanColumn({ id, config, leads, count, isOver, selectMode, selectedIds, onToggleSelect, onSelectAll, onNotify }: {
  id: string
  config: { label: string; color: string; bg: string }
  leads: Lead[]
  count: number
  isOver: boolean
  selectMode: boolean
  selectedIds: Set<number>
  onToggleSelect: (id: number) => void
  onSelectAll: (ids: number[]) => void
  onNotify?: () => void
}) {
  const { setNodeRef } = useDroppable({ id })
  const allSelected = leads.length > 0 && leads.every(l => selectedIds.has(l.id))
  const hasStudentLeads = leads.some(l => l.student)

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[280px] w-[280px] rounded-xl border transition-colors ${isOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'}`}
    >
      {/* Column Header */}
      <div className="flex items-center gap-2 p-3 border-b">
        {selectMode && hasStudentLeads && (
          <button
            onClick={() => {
              const studentLeadIds = leads.filter(l => l.student).map(l => l.id)
              if (allSelected) {
                studentLeadIds.forEach(id => onToggleSelect(id))
              } else {
                onSelectAll(studentLeadIds)
              }
            }}
            className="flex-shrink-0"
          >
            {allSelected
              ? <CheckSquare className="h-4 w-4 text-primary" />
              : <Square className="h-4 w-4 text-muted-foreground" />
            }
          </button>
        )}
        <div className={`w-2.5 h-2.5 rounded-full ${config.bg}`} />
        <span className="text-sm font-semibold flex-1">{config.label}</span>
        <Badge variant="secondary" className="text-xs px-1.5 py-0">
          {count}
        </Badge>
        {onNotify && !selectMode && (
          <button onClick={onNotify} className="text-muted-foreground hover:text-primary transition-colors" title="Notificar todos">
            <Bell className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-320px)] min-h-[100px]">
        {leads.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-xs text-muted-foreground">
            Nenhum lead
          </div>
        ) : (
          leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              selectMode={selectMode}
              isSelected={selectedIds.has(lead.id)}
              onToggleSelect={() => onToggleSelect(lead.id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Draggable Lead Card
function LeadCard({ lead, isDragging, selectMode, isSelected, onToggleSelect }: {
  lead: Lead
  isDragging?: boolean
  selectMode: boolean
  isSelected: boolean
  onToggleSelect: () => void
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: lead.id.toString(),
    data: { lead },
    disabled: selectMode,
  })

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    zIndex: 50,
  } : undefined

  const tempConfig = lead.temperature ? TEMP_ICON[lead.temperature] : null
  const TempIcon = tempConfig?.icon

  const handleClick = () => {
    if (selectMode) {
      if (lead.student) onToggleSelect()
      return
    }
    router.visit(`/crm/${lead.id}`)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group rounded-lg border bg-card p-3 shadow-sm transition-shadow hover:shadow-md cursor-pointer ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : ''} ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-2">
        {selectMode && (
          <div className="flex-shrink-0 mt-0.5">
            {lead.student ? (
              isSelected
                ? <CheckSquare className="h-4 w-4 text-primary" />
                : <Square className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Square className="h-4 w-4 text-muted-foreground/30" />
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{lead.name}</p>
          {lead.phone && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Phone className="h-3 w-3" />{lead.phone}
            </p>
          )}
        </div>
        {!selectMode && (
          <div
            {...attributes}
            {...listeners}
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {lead.source_label && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{lead.source_label}</Badge>
        )}
        {TempIcon && (
          <TempIcon className={`h-3.5 w-3.5 ${tempConfig?.color}`} />
        )}
        {lead.reason && (
          <span className="text-[10px] text-red-500 truncate">{lead.reason}</span>
        )}
      </div>

      {lead.lesson_package && (
        <p className="text-[10px] text-muted-foreground mt-1.5 truncate">
          {lead.lesson_package.name}
        </p>
      )}

      <p className="text-[10px] text-muted-foreground mt-1">{lead.created_at}</p>
    </div>
  )
}

// Overlay card shown while dragging
function DragOverlayCard({ lead }: { lead: Lead }) {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-xl ring-2 ring-primary w-[260px]">
      <p className="text-sm font-medium truncate">{lead.name}</p>
      {lead.phone && (
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
          <Phone className="h-3 w-3" />{lead.phone}
        </p>
      )}
    </div>
  )
}

export default function CrmIndex({
  columns, column_order, status_counts, retention_new_count, pipeline,
  search: initialSearch, reason_filter, package_filter, available_packages
}: Props) {
  const [searchValue, setSearchValue] = useState(initialSearch || '')
  const [boardColumns, setBoardColumns] = useState(columns)
  const [activeLead, setActiveLead] = useState<Lead | null>(null)
  const [overColumn, setOverColumn] = useState<string | null>(null)

  // Modo seleção
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkTitle, setBulkTitle] = useState('')
  const [bulkBody, setBulkBody] = useState('')
  const [sending, setSending] = useState(false)

  // Filtros
  const [showFilters, setShowFilters] = useState(!!(reason_filter || package_filter))

  useEffect(() => {
    setBoardColumns(columns)
  }, [columns])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  useEffect(() => {
    const delay = setTimeout(() => {
      if (searchValue !== (initialSearch || '')) {
        router.get('/crm', buildParams({ search: searchValue }), { preserveState: true })
      }
    }, 500)
    return () => clearTimeout(delay)
  }, [searchValue])

  const buildParams = (overrides: Record<string, string | null | undefined> = {}) => {
    const params: Record<string, string> = { pipeline }
    if (searchValue) params.search = searchValue
    if (reason_filter) params.reason_filter = reason_filter
    if (package_filter) params.package_filter = package_filter
    Object.entries(overrides).forEach(([k, v]) => {
      if (v) params[k] = v
      else delete params[k]
    })
    return params
  }

  const handleTabChange = (newPipeline: string) => {
    setSelectMode(false)
    setSelectedIds(new Set())
    router.get('/crm', { pipeline: newPipeline }, { preserveState: false })
  }

  const handleFilterChange = (key: string, value: string | null) => {
    router.get('/crm', buildParams({ [key]: value }), { preserveState: false })
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = (ids: number[]) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.add(id))
      return next
    })
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelectedIds(new Set())
    setBulkTitle('')
    setBulkBody('')
  }

  const handleBulkNotify = async () => {
    if (!bulkTitle.trim() || !bulkBody.trim() || selectedIds.size === 0) return
    setSending(true)

    try {
      const res = await fetch('/crm/bulk_notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          lead_ids: Array.from(selectedIds),
          title: bulkTitle,
          body: bulkBody,
        }),
      })
      const data = await res.json()
      if (data.success) {
        exitSelectMode()
        router.reload()
      }
    } finally {
      setSending(false)
    }
  }

  // Notificar coluna inteira
  const handleNotifyColumn = (status: string) => {
    const leads = boardColumns[status] || []
    const withStudent = leads.filter(l => l.student)
    if (withStudent.length === 0) return

    setSelectMode(true)
    setSelectedIds(new Set(withStudent.map(l => l.id)))
  }

  const handleDragStart = (event: DragStartEvent) => {
    const lead = event.active.data.current?.lead as Lead
    setActiveLead(lead)
  }

  const handleDragOver = (event: any) => {
    setOverColumn(event.over?.id?.toString() || null)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveLead(null)
    setOverColumn(null)

    const { active, over } = event
    if (!over) return

    const leadId = active.id.toString()
    const newStatus = over.id.toString()
    const lead = active.data.current?.lead as Lead

    if (!lead || lead.status === newStatus) return
    if (!column_order.includes(newStatus)) return

    // Optimistic update
    const newColumns = { ...boardColumns }
    newColumns[lead.status] = newColumns[lead.status].filter(l => l.id.toString() !== leadId)
    newColumns[newStatus] = [{ ...lead, status: newStatus }, ...newColumns[newStatus]]
    setBoardColumns(newColumns)

    // Persist
    fetch(`/crm/${leadId}/move`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ status: newStatus }),
    }).catch(() => {
      setBoardColumns(columns)
    })
  }

  const isAcquisition = pipeline === 'acquisition'
  const isRetention = pipeline === 'retention'
  const colConfig = isAcquisition ? COLUMN_CONFIG : RETENTION_COLUMN_CONFIG
  const totalOpen = column_order
    .filter(s => s !== 'converted' && s !== 'lost')
    .reduce((sum, s) => sum + (status_counts[s] || 0), 0)

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 space-y-4 h-[calc(100vh-56px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">CRM</h1>
              <p className="text-sm text-muted-foreground">
                {totalOpen} lead{totalOpen !== 1 ? 's' : ''} em aberto
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar lead..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
            </Button>
            <Button
              variant={selectMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
            >
              <CheckSquare className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => router.visit('/crm/settings')}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filtros */}
        {showFilters && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {isRetention && (
              <Select
                value={reason_filter || 'all'}
                onValueChange={(v) => handleFilterChange('reason_filter', v === 'all' ? null : v)}
              >
                <SelectTrigger className="w-[200px] h-9">
                  <SelectValue placeholder="Motivo do risco" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os motivos</SelectItem>
                  <SelectItem value="inactive">Sumido</SelectItem>
                  <SelectItem value="low_lessons">Poucas aulas</SelectItem>
                  <SelectItem value="expiring">Vencendo</SelectItem>
                </SelectContent>
              </Select>
            )}

            {available_packages.length > 0 && (
              <Select
                value={package_filter || 'all'}
                onValueChange={(v) => handleFilterChange('package_filter', v === 'all' ? null : v)}
              >
                <SelectTrigger className="w-[200px] h-9">
                  <SelectValue placeholder="Pacote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os pacotes</SelectItem>
                  {available_packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id.toString()}>{pkg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {(reason_filter || package_filter) && (
              <Button variant="ghost" size="sm" onClick={() => {
                router.get('/crm', { pipeline }, { preserveState: false })
              }}>
                <X className="h-4 w-4 mr-1" />
                Limpar filtros
              </Button>
            )}
          </div>
        )}

        {/* Pipeline Tabs */}
        <Tabs value={pipeline} onValueChange={handleTabChange} className="flex-shrink-0">
          <TabsList>
            <TabsTrigger value="acquisition" className="gap-2">
              <UserPlus className="h-4 w-4" />
              Aquisição
            </TabsTrigger>
            <TabsTrigger value="retention" className="gap-2 relative">
              <ShieldAlert className="h-4 w-4" />
              Retenção
              {retention_new_count > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {retention_new_count}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Barra de ações em massa */}
        {selectMode && (
          <div className="flex-shrink-0 border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">
                  {selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selecionado{selectedIds.size !== 1 ? 's' : ''}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={exitSelectMode}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {selectedIds.size > 0 && (
              <div className="flex gap-3 items-end">
                <div className="flex-1 space-y-2">
                  <Input
                    value={bulkTitle}
                    onChange={(e) => setBulkTitle(e.target.value)}
                    placeholder="Título da notificação"
                    className="h-9"
                  />
                  <Textarea
                    value={bulkBody}
                    onChange={(e) => setBulkBody(e.target.value)}
                    placeholder="Mensagem..."
                    rows={2}
                  />
                </div>
                <Button
                  onClick={handleBulkNotify}
                  disabled={!bulkTitle.trim() || !bulkBody.trim() || sending}
                  className="h-9"
                >
                  <Send className="h-4 w-4 mr-1" />
                  {sending ? 'Enviando...' : `Enviar para ${selectedIds.size}`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Kanban Board */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div className="flex gap-4 h-full pb-4">
              {column_order.map((status) => {
                const config = colConfig[status]
                if (!config) return null
                const leads = boardColumns[status] || []
                const hasStudentLeads = leads.some(l => l.student)
                return (
                  <div key={status} className="flex flex-col min-w-[280px] w-[280px]">
                    <KanbanColumn
                      id={status}
                      config={config}
                      leads={leads}
                      count={status_counts[status] || 0}
                      isOver={overColumn === status}
                      selectMode={selectMode}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelect}
                      onSelectAll={selectAll}
                      onNotify={hasStudentLeads && status !== 'converted' && status !== 'lost' ? () => handleNotifyColumn(status) : undefined}
                    />
                  </div>
                )
              })}
            </div>
          </div>

          <DragOverlay>
            {activeLead ? <DragOverlayCard lead={activeLead} /> : null}
          </DragOverlay>
        </DndContext>
      </div>
    </DashboardLayout>
  )
}
